import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  NgbDateAdapter,
  NgbDateNativeAdapter,
  NgbDateParserFormatter,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { ProposalRequestFileForm } from 'src/app/classes/form-groups/proposal-request-file-form';
import { ProposalRequestForm } from 'src/app/classes/form-groups/proposal-request-form';
import { ProposalRequestLineItemForm } from 'src/app/classes/form-groups/proposal-request-line-item.form';
import { File as FileModel, ProposalRequestFile } from 'src/app/classes/models';
import { CustomNgbDateFormatter } from 'src/app/dependencies/custom-ngb-date-formatter';
import { ProposalRequestFileUploadModalComponent } from 'src/app/modals/proposal-request-file-upload-modal/proposal-request-file-upload-modal.component';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-quicksource-date-pricing-instructions',
  templateUrl: './quicksource-date-pricing-instructions.component.html',
  styleUrls: ['./quicksource-date-pricing-instructions.component.less'],
  providers: [
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceDatePricingInstructionsComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private route: ActivatedRoute,
    private ModalService: NgbModal
  ) {
    this.SharedDataService.listenForEvent<
      ProposalRequestForm | boolean
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'SetProposalRequestForm' &&
          typeof data !== 'boolean'
        ) {
          this.ProposalRequestFormGroup = data;
        }

        if (eventName === 'SetDisableInput' && typeof data === 'boolean') {
          this.disableInput = data;
        }
      }
    });

    this.SharedDataService.sendEvent('GetProposalRequestForm');
    this.SharedDataService.sendEvent('GetDisableInput');

    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const ProposalRequestId = Number(paramMap.get('ProposalRequestId'));

        if (!ProposalRequestId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Proposal Request Id.',
            { returnError: true }
          );
        }

        this.ProposalRequestId = ProposalRequestId;
      }
    });
  }

  ProposalRequestId?: number;
  ProposalRequestFormGroup?: ProposalRequestForm;
  disableInput: boolean = false;

  BidDueMinDate = { day: 1, month: 1, year: 1900 };
  BidStartMinDate = { day: 1, month: 1, year: 1900 };

  bidReminderDateChanged() {
    if (this.ProposalRequestFormGroup) {
      const {
        value: { reminderDate }
      } = this.ProposalRequestFormGroup;

      Object.assign(this.BidDueMinDate, {
        day: (reminderDate?.getDate() || 1) + 1,
        month: (reminderDate?.getMonth() || 0) + 1,
        year: reminderDate?.getFullYear() || 1900
      });
    }
  }

  bidDueDateChanged() {
    if (this.ProposalRequestFormGroup) {
      const {
        value: { dueDate }
      } = this.ProposalRequestFormGroup;

      Object.assign(this.BidStartMinDate, {
        day: (dueDate?.getDate() || 1) + 1,
        month: (dueDate?.getMonth() || 0) + 1,
        year: dueDate?.getFullYear() || 1900
      });
    }
  }

  generateProposalRequestLineItemForm() {
    return new ProposalRequestLineItemForm();
  }

  multiLineToggle() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request Form.',
        { returnError: true }
      );
    }

    const multiLine = this.ProposalRequestFormGroup.controls['multiLine'].value;
    const ProposalRequestLineItemFormArray =
      this.ProposalRequestFormGroup.controls['ProposalRequestLineItems'];

    if (multiLine && ProposalRequestLineItemFormArray.length === 0) {
      for (let i = 0; i < 3; i += 1) {
        ProposalRequestLineItemFormArray.push(
          this.generateProposalRequestLineItemForm()
        );
      }
    }

    if (!multiLine) {
      ProposalRequestLineItemFormArray.clear();
    }
  }

  addProposalRequestLineItem() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request Form.',
        { returnError: true }
      );
    }

    const ProposalRequestLineItemsFormArray =
      this.ProposalRequestFormGroup.controls['ProposalRequestLineItems'];

    if (ProposalRequestLineItemsFormArray.length === 10) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot add more than 10 Line Items.',
        { returnError: true }
      );
    }

    ProposalRequestLineItemsFormArray.push(
      this.generateProposalRequestLineItemForm()
    );
  }

  removeProposalRequestLineItem(index: number) {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request Form.',
        { returnError: true }
      );
    }

    const ProposalRequestLineItemsFormArray =
      this.ProposalRequestFormGroup.controls['ProposalRequestLineItems'];

    if (ProposalRequestLineItemsFormArray.length === 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot remove last line item.',
        { returnError: true }
      );
    }

    ProposalRequestLineItemsFormArray.removeAt(index);
  }

  openFileSelectionModal() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request Form.',
        { returnError: true }
      );
    }

    const ProposalRequestFileUploadModal = this.ModalService.open(
      ProposalRequestFileUploadModalComponent,
      {
        backdrop: 'static',
        modalDialogClass: 'proposal-request-file-upload-modal'
      }
    );
    const { componentInstance } = ProposalRequestFileUploadModal;

    const ProposalRequest = this.ProposalRequestFormGroup.value;
    const { ProposalRequestFiles } = ProposalRequest;

    Object.assign(componentInstance, {
      title: ProposalRequest.name,
      ProposalRequest,
      ProposalRequestFiles
    });

    from(ProposalRequestFileUploadModal.result).subscribe({
      next: (ProposalRequestFiles: ProposalRequestFile[]) => {
        const ProposalRequestFilesFormArray =
          this.ProposalRequestFormGroup!.controls['ProposalRequestFiles'];
        ProposalRequestFilesFormArray.clear();
        ProposalRequestFiles.forEach((prf) => {
          const ProposalRequestFileFormGroup = new ProposalRequestFileForm();
          ProposalRequestFileFormGroup.patchValue(prf);
          ProposalRequestFilesFormArray.push(ProposalRequestFileFormGroup);
        });
        this._cdr.markForCheck();
      }
    });
  }

  downloadProposalRequestFile(ProposalRequestFileRecord: ProposalRequestFile) {
    this.SharedDataService.sendEvent(
      'DownloadProposalRequestFile',
      new ProposalRequestFile(ProposalRequestFileRecord)
    );
  }

  ngOnInit(): void {}
}
